import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { palette } from '../../theme';

/**
 * Mobile bottom navigation with a floating action interaction.
 * The primary action floats above the rail and expands into a soft glow.
 */
export default function MobileNav({ items, action }) {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const visible = items.slice(0, 4);

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 1250,
        display: { xs: 'flex', lg: 'none' },
        alignItems: 'center',
        justifyContent: 'space-around',
        px: 1.25,
        py: 1,
        borderRadius: 4,
        border: `1px solid ${alpha(palette.primary, 0.16)}`,
        background: `linear-gradient(180deg, ${alpha('#0C1428', 0.94)}, ${alpha('#050816', 0.96)})`,
        backdropFilter: 'blur(22px) saturate(140%)',
        WebkitBackdropFilter: 'blur(22px) saturate(140%)',
        boxShadow: '0 24px 60px -28px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >
      {visible.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);
        return (
          <Box
            key={item.key}
            component={Link}
            to={item.path}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.35,
              px: 1.25,
              py: 0.6,
              borderRadius: 2.5,
              textDecoration: 'none',
              color: active ? palette.primary : 'text.secondary',
              background: active ? alpha(palette.primary, 0.12) : 'transparent',
              border: `1px solid ${active ? alpha(palette.primary, 0.28) : 'transparent'}`,
              transition: 'background-color 220ms ease, color 220ms ease',
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.04em' }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}

      {action && (
        <Box
          component={Link}
          to={action.path}
          aria-label={action.label}
          sx={{
            position: 'absolute',
            right: 18,
            top: -30,
            width: 54,
            height: 54,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            textDecoration: 'none',
            background: `linear-gradient(140deg, ${palette.primary}, ${palette.secondary})`,
            color: '#04070F',
            boxShadow: `0 18px 36px -14px ${alpha(palette.primary, 0.95)}, 0 0 0 6px ${alpha('#050816', 0.85)}`,
            '&:hover': { transform: 'translateY(-2px)' },
            transition: 'transform 220ms ease',
          }}
        >
          {action.icon}
        </Box>
      )}
    </Box>
  );
}
